import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Animated, Easing, useWindowDimensions } from 'react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { useThemeContext } from '../../../src/theme/ThemeContext';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';
import { CompetitionFuturesPlate } from './CompetitionFuturesPlate';
import { FuturesPickCard } from '../../worldcup/PicksView/FuturesPickCard';
import { FuturesPickerModal } from '../../worldcup/PicksView/FuturesPickerModal';
import { getFuturesCardStyles } from '../../worldcup/PicksView/FuturesCard.styles';
import {
  FUTURES_CARD_COPY,
  FUTURES_CARD_NARROW_WIDTH,
  FUTURES_CARD_TIGHT_WIDTH,
  type PickerType,
} from '../../worldcup/PicksView/FuturesCard.constants';
import {
  resolveWinnerParticipant,
  scorerKitMetaForCard,
  scorerTextLabelForId,
} from '../../worldcup/PicksView/futuresCardDisplay.helper';
import type {
  WorldCupFuturesOptions,
  WorldCupFuturesStats,
} from '../../../src/worldcup/helpers/worldcupPicksData.helper';
import type { FuturesPick } from '../../../src/worldcup/hooks/useWorldCupPicksData';
import {
  COMPETITION_FUTURES_COPY,
  COMPETITION_FUTURES_TEST_IDS,
} from './CompetitionFutures.constants';

export interface CompetitionFuturesViewProps {
  futures: FuturesPick | null;
  /** False once the competition's own first fixture has kicked off (AC-15.5). */
  isEditable: boolean;
  futuresOptions: WorldCupFuturesOptions | null;
  futuresStats: WorldCupFuturesStats | null;
  onSave: (
    winnerTeamId: string | null | undefined,
    topScorerPlayerId: string | null | undefined,
  ) => Promise<void>;
}

/**
 * Winner + top-scorer picks for a competition other than the World Cup
 * (specs/champions-league Story 15).
 *
 * Deliberately a sibling of `FuturesCard` rather than a parameterisation of it. The two share
 * everything presentational — `FuturesPickCard`, `FuturesPickerModal`, `getFuturesCardStyles`,
 * the display resolvers — and differ in exactly the two places that matter: the World Cup card
 * is wired to World Cup constants and a World-Cup-scoped stats hook, and it draws the country
 * flag **as the whole square**. A club draws its kit centred on that flag instead
 * (AC-15.11, which reversed the original "no flags on a club competition"), through
 * `CompetitionFuturesPlate` and its own styles. Editing the shipped card to take both shapes
 * would have put "the World Cup is unchanged" (AC-15.10) at the mercy of a test; this way it
 * holds by construction — the shared style file is read here, never written.
 */
export const CompetitionFuturesView: React.FC<CompetitionFuturesViewProps> = ({
  futures,
  isEditable,
  futuresOptions,
  futuresStats,
  onSave,
}) => {
  const theme = useTheme();
  const { themeMode } = useThemeContext();
  const { width: windowWidth } = useWindowDimensions();
  const styles = useMemo(
    () => getFuturesCardStyles(theme, windowWidth, themeMode),
    [theme, windowWidth, themeMode],
  );
  const layoutNarrow = windowWidth < FUTURES_CARD_NARROW_WIDTH;
  const layoutTight = windowWidth < FUTURES_CARD_TIGHT_WIDTH;

  const [draftWinner, setDraftWinner] = useState<string | null>(futures?.winnerTeamId ?? null);
  const [draftScorer, setDraftScorer] = useState<string | null>(futures?.topScorerPlayerId ?? null);
  const [activePicker, setActivePicker] = useState<PickerType | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDraftWinner(futures?.winnerTeamId ?? null);
    setDraftScorer(futures?.topScorerPlayerId ?? null);
  }, [futures?.winnerTeamId, futures?.topScorerPlayerId]);

  const locked = !isEditable;
  const winnerSettled = futures?.winnerCorrect != null;
  const scorerSettled = futures?.topScorerCorrect != null;
  const showLockedCaption = locked && (!winnerSettled || !scorerSettled);

  const noTeams = (futuresOptions?.participantTeams.length ?? 0) === 0;
  const noScorers = (futuresOptions?.topScorers?.length ?? 0) === 0
    && !futuresOptions?.topScorerOtherId;
  const winnerDisabled = winnerSettled || locked || noTeams;
  const scorerDisabled = scorerSettled || locked || noScorers;

  const winnerTeam = useMemo(
    () => resolveWinnerParticipant(draftWinner, futuresOptions?.participantTeams ?? []),
    [draftWinner, futuresOptions?.participantTeams],
  );
  const winnerKit = useMemo(
    () => (winnerTeam ? getTeamKitConfig(winnerTeam.name) : null),
    [winnerTeam],
  );
  const scorerLabel = useMemo(
    () => scorerTextLabelForId(draftScorer, futuresOptions),
    [draftScorer, futuresOptions],
  );
  const scorerKit = useMemo(
    () => scorerKitMetaForCard(draftScorer, futuresOptions),
    [draftScorer, futuresOptions],
  );

  const cardOpacity = useRef(new Animated.Value(0)).current;
  const modalTranslateY = useRef(new Animated.Value(300)).current;
  const winnerScale = useRef(new Animated.Value(1)).current;
  const scorerScale = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    Animated.timing(cardOpacity, { toValue: 1, duration: 400, useNativeDriver: true }).start();
  }, [cardOpacity]);

  const closePicker = useCallback(() => setActivePicker(null), []);

  useEffect(() => {
    if (locked) closePicker();
  }, [locked, closePicker]);

  const pressPickCard = useCallback(
    (kind: PickerType) => {
      if (kind === 'winner' ? winnerDisabled : scorerDisabled) return;
      const anim = kind === 'winner' ? winnerScale : scorerScale;
      Animated.sequence([
        Animated.spring(anim, { toValue: 0.96, useNativeDriver: true, speed: 60, bounciness: 0 }),
        Animated.spring(anim, { toValue: 1, useNativeDriver: true, speed: 50, bounciness: 6 }),
      ]).start();
      modalTranslateY.setValue(300);
      setActivePicker(kind);
      Animated.timing(modalTranslateY, {
        toValue: 0,
        duration: 280,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start();
    },
    [modalTranslateY, scorerDisabled, scorerScale, winnerDisabled, winnerScale],
  );

  const select = useCallback(
    async (kind: PickerType, id: string) => {
      if (isSaving) return;
      if (kind === 'winner' ? winnerDisabled : scorerDisabled) return;
      if (kind === 'winner') setDraftWinner(id);
      else setDraftScorer(id);
      closePicker();
      setIsSaving(true);
      try {
        await onSave(
          kind === 'winner' ? id : undefined,
          kind === 'scorer' ? id : undefined,
        );
      } finally {
        setIsSaving(false);
      }
    },
    [closePicker, isSaving, onSave, scorerDisabled, winnerDisabled],
  );

  const selectWinner = useCallback((id: string) => select('winner', id), [select]);
  const selectScorer = useCallback((id: string) => select('scorer', id), [select]);

  /**
   * Clubs wear kits, on the flag of the country they play in (AC-15.11). The winner square
   * takes the *club's* country; the scorer square takes the *player's* nationality, which is
   * the field the option already carries and the one the World Cup scorer square reads.
   */
  const winnerMedia = (
    <CompetitionFuturesPlate
      pickKind="winner"
      countryCode={winnerTeam?.countryCode}
      kit={winnerKit}
      windowWidth={windowWidth}
      accessibilityLabel={winnerTeam?.name}
    />
  );

  const scorerMedia = (
    <CompetitionFuturesPlate
      pickKind="scorer"
      countryCode={scorerKit?.countryCode}
      kit={{
        primary: scorerKit?.kitColor ?? theme.gray[400],
        secondary: scorerKit?.trimColor ?? theme.gray[300],
        number: scorerKit?.number,
      }}
      windowWidth={windowWidth}
      accessibilityLabel={scorerLabel ?? undefined}
    />
  );

  return (
    <Animated.View
      style={[styles.card, { opacity: cardOpacity }]}
      testID={COMPETITION_FUTURES_TEST_IDS.root}
    >
      <View style={styles.picksRow}>
        <FuturesPickCard
          pickKind="winner"
          label={COMPETITION_FUTURES_COPY.WINNER_LABEL}
          media={winnerMedia}
          nameText={winnerTeam?.name ?? null}
          namePlaceholder={FUTURES_CARD_COPY.PICK_TEAM}
          pickerDisabled={winnerDisabled}
          // A settled pick shows its verdict instead of a padlock. Both are true at once once
          // the competition is over, and "you were right" says strictly more than "you cannot
          // change this" about a pick that can no longer change anyway.
          isLocked={locked && !winnerSettled}
          settled={winnerSettled}
          settledCorrect={futures?.winnerCorrect}
          scale={winnerScale}
          onPress={() => pressPickCard('winner')}
          layoutTight={layoutTight}
          layoutNarrow={layoutNarrow}
          styles={styles}
          theme={theme}
        />
        <FuturesPickCard
          pickKind="scorer"
          label={COMPETITION_FUTURES_COPY.TOP_SCORER_LABEL}
          media={scorerMedia}
          nameText={scorerLabel}
          namePlaceholder={FUTURES_CARD_COPY.PICK_PLAYER}
          pickerDisabled={scorerDisabled}
          isLocked={locked && !scorerSettled}
          settled={scorerSettled}
          settledCorrect={futures?.topScorerCorrect}
          scale={scorerScale}
          onPress={() => pressPickCard('scorer')}
          layoutTight={layoutTight}
          layoutNarrow={layoutNarrow}
          styles={styles}
          theme={theme}
        />
      </View>

      {showLockedCaption ? (
        <View style={styles.lockedCaption} testID={COMPETITION_FUTURES_TEST_IDS.locked}>
          <Text style={styles.lockedCaptionText}>
            {COMPETITION_FUTURES_COPY.LOCKED_CAPTION}
          </Text>
        </View>
      ) : (
        <View style={styles.picksRowBottomSpacer} />
      )}

      <FuturesPickerModal
        activePicker={activePicker}
        isSaving={isSaving}
        draftWinner={draftWinner}
        draftScorer={draftScorer}
        modalTranslateY={modalTranslateY}
        pickerTitle={
          activePicker === 'winner'
            ? COMPETITION_FUTURES_COPY.MODAL_WINNER
            : COMPETITION_FUTURES_COPY.MODAL_SCORER
        }
        participantTeams={futuresOptions?.participantTeams ?? []}
        topScorerOptions={futuresOptions?.topScorers ?? []}
        topScorerOtherId={futuresOptions?.topScorerOtherId}
        futuresStats={futuresStats}
        winnerVisual="kit"
        onClose={closePicker}
        onSelectWinner={selectWinner}
        onSelectScorer={selectScorer}
      />
    </Animated.View>
  );
};
